import React, { useMemo } from 'react';
import { styled } from '@components-shared/src/stitches';
import { Grid } from '@components/basics/Grid';
import { GridElem } from '@components/basics/GridElem';
import { useGridColumnAmount } from '@components/layout/useColumnAmount';
import { GridCarousel } from '@components/main-components/carousel/GridCarousel';
import { DisplayToggle } from '@components/shared';
import { MediaQuery } from '@context/mediaQuery';
import type { FragmentType } from '@graphql/reactQuery';
import { getFragment } from '@graphql/reactQuery';
import { CollectorCardInfoFragmentDoc } from '@graphql/reactQuery';
import { CollectorCard } from './Cards/CollectorCard';
import { UnclaimedCard } from './Cards/UnclaimedCard';
import { CollectorCardSkeleton } from './Cards/styles';
import type { CollectorsSort } from '.';

export const NUM_BACKED_ENTITIES_PREVIEW = 8;

export const CollectorsSection = ({
  data,
  loading,
  sort,
  variant = 'collector',
}: {
  data: FragmentType<CollectorCardInfoFragmentDoc>[] | undefined;
  loading: boolean;
  sort: CollectorsSort;
  variant: 'collector' | 'artistBacked';
}) => {
  const { laptop } = MediaQuery.useContainer();
  const columnAmount = useGridColumnAmount();

  const dataSize = data?.length || 0;
  /**
   * Determine the number of unclaimed cards to show on the screen.
   */
  const hasMaximumEntities = dataSize >= NUM_BACKED_ENTITIES_PREVIEW;
  const FILLER_COUNT = !hasMaximumEntities ? NUM_BACKED_ENTITIES_PREVIEW - dataSize : 0;
  const FILLER_INDEX = dataSize + 1;

  const collectorsSlides = useMemo(() => {
    const collectorsDataSlides =
      data?.map((collector, index) => {
        const id = getFragment(CollectorCardInfoFragmentDoc, collector).id;
        return {
          id,
          slide: (
            <CollectorCard
              key={id}
              collector={collector}
              index={index + 1}
              sort={sort}
              variant={variant}
              loading={loading}
              seeAllPage={false}
            />
          ),
        };
      }) || [];

    return hasMaximumEntities
      ? collectorsDataSlides
      : [
          ...collectorsDataSlides,
          ...Array.from({ length: FILLER_COUNT }).map((_, i) => {
            return {
              id: `${i}`,
              slide: <UnclaimedCard key={FILLER_INDEX + i} index={FILLER_INDEX + i} />,
            };
          }),
        ];
  }, [FILLER_COUNT, FILLER_INDEX, data, hasMaximumEntities, sort, variant, loading]);

  if (loading) {
    return (
      <>
        <DisplayToggle media="laptop">
          <Grid
            className="gap-5 sm:[&>*:nth-child(n+7)]:hidden xl:[&>*:nth-child(n+7)]:block"
            columns={columnAmount}
            align="end"
          >
            {Array.from({ length: NUM_BACKED_ENTITIES_PREVIEW }).map((_, i) => {
              return (
                <CustomGridElem key={i}>
                  <CollectorCardSkeleton />
                </CustomGridElem>
              );
            })}
          </Grid>
        </DisplayToggle>
        <DisplayToggle media="notLaptop">
          <GridCarousel
            slides={CAROUSEL_LOADER}
            onEndReached={null}
            onInternalActiveIndexChange={null}
            columnAmount={null}
            hideArrows={false}
            externalActiveIndex={null}
            spacing={null}
            alignSlidesLeft={false}
            ssrSlideWidth={undefined}
            columnType="default"
          />
        </DisplayToggle>
      </>
    );
  }

  return laptop ? (
    <Grid
      className="gap-5 sm:[&>*:nth-child(n+7)]:hidden xl:[&>*:nth-child(n+7)]:block"
      columns={columnAmount}
      align="end"
    >
      {data?.map((collector, index) => (
        <CollectorCard
          key={index}
          collector={collector}
          index={index + 1}
          sort={sort}
          variant={variant}
          loading={loading}
          seeAllPage={false}
        />
      ))}
      {!hasMaximumEntities &&
        Array.from({ length: FILLER_COUNT }).map((_, i) => {
          return <UnclaimedCard key={FILLER_INDEX + i} index={FILLER_INDEX + i} />;
        })}
    </Grid>
  ) : (
    <GridCarousel
      slides={collectorsSlides}
      onEndReached={null}
      onInternalActiveIndexChange={null}
      columnAmount={null}
      hideArrows={false}
      externalActiveIndex={null}
      spacing={null}
      alignSlidesLeft={false}
      ssrSlideWidth={undefined}
      columnType="default"
    />
  );
};

const CustomGridElem = styled(GridElem, {
  marginBottom: 0,
});

const SliderLoader = styled('div', {
  marginX: 6,
});

const CAROUSEL_LOADER = Array.from({ length: 4 }).map((_, i) => {
  return {
    id: `${i}`,
    slide: (
      <SliderLoader key={i}>
        <CollectorCardSkeleton />
      </SliderLoader>
    ),
  };
});
